import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ChevronDown } from 'lucide-react';

/**
 * Composant FAQ optimisé pour l'AEO (Answer Engine Optimization)
 * - Questions en h3 pour une hiérarchie SEO correcte
 * - Réponses concises et directes
 * - Balisage Schema.org FAQPage intégré
 * - Accessible et responsive
 */
const FAQ = () => {
  const { i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  // FAQ data avec traductions
  const faqData = {
    fr: {
      sectionTitle: "Questions fréquentes",
      sectionSubtitle: "Tout ce que vous devez savoir sur nos services",
      items: [
        {
          question: "Quels types d'entreprises accompagnez-vous ?",
          answer: "Nous accompagnons les startups, scale-ups et PME B2B de 10 à 500 collaborateurs. Nos clients sont principalement dans la Tech, le SaaS, les services professionnels et l'industrie. Nous travaillons avec des entreprises qui ont une ambition de croissance forte et qui cherchent à structurer ou accélérer leur développement commercial."
        },
        {
          question: "Combien de rendez-vous qualifiés générez-vous par mois ?",
          answer: "Notre service de prospection externalisée génère en moyenne 20 à 30 rendez-vous qualifiés par mois. Ce volume dépend de votre cible, de votre secteur et de la complexité de votre offre. Chaque RDV est qualifié selon vos critères : décisionnaire confirmé, budget identifié, besoin exprimé."
        },
        {
          question: "Où sont situés vos bureaux ?",
          answer: "Nous avons deux bureaux : Paris (Spaces Saint-Lazare, 75008) et Genève (Spaces Quai de l'Île, 1204). Nous intervenons dans toute la France, la Suisse, la Belgique et le Luxembourg. La plupart de nos missions se font en hybride : présence terrain régulière et travail à distance."
        },
        {
          question: "Quels résultats obtenez-vous pour vos clients ?",
          answer: "En moyenne, nos clients constatent +47% d'augmentation de CA, une accélération x3 des cycles de vente, et 89% de taux de rétention. Nous avons généré plus de 10M€ d'opportunités commerciales pour nos clients depuis notre création."
        },
        {
          question: "Quelle est la durée d'engagement minimum ?",
          answer: "Pour le Sparring Partner, nous proposons un programme de 12 mois annulable avec 4 semaines de préavis. Pour les autres services, la durée varie : 3 à 6 mois pour l'architecture commerciale, missions ponctuelles ou récurrentes pour le coaching. Nous définissons ensemble le format adapté à vos besoins."
        },
        {
          question: "Travaillez-vous uniquement en français ?",
          answer: "Non, nous intervenons en français et en anglais. Notre équipe accompagne des clients internationaux et peut animer des formations, coacher des équipes et prospecter dans les deux langues. Nous avons l'habitude des contextes multiculturels."
        },
        {
          question: "Comment démarrer une collaboration avec FOMOW ?",
          answer: "Réservez un appel découverte de 30 minutes via notre calendrier en ligne. Nous analysons vos enjeux, votre contexte et vos objectifs. Si ça matche, nous vous proposons un plan d'accompagnement personnalisé sous 48h. Zéro engagement avant signature."
        },
        {
          question: "Quels outils et CRM utilisez-vous ?",
          answer: "Nous nous adaptons à votre stack existant : HubSpot, Salesforce, Pipedrive, Notion, etc. Si vous n'avez pas de CRM, nous pouvons vous accompagner dans le choix et la mise en place. Nous utilisons également des outils de prospection comme Lemlist, Apollo, LinkedIn Sales Navigator."
        }
      ]
    },
    en: {
      sectionTitle: "Frequently Asked Questions",
      sectionSubtitle: "Everything you need to know about our services",
      items: [
        {
          question: "What types of companies do you work with?",
          answer: "We work with B2B startups, scale-ups and SMEs with 10 to 500 employees. Our clients are mainly in Tech, SaaS, professional services and industry. We work with companies that have strong growth ambitions and are looking to structure or accelerate their commercial development."
        },
        {
          question: "How many qualified meetings do you generate per month?",
          answer: "Our outsourced prospecting service generates an average of 20 to 30 qualified meetings per month. This volume depends on your target, sector and offer complexity. Each meeting is qualified according to your criteria: confirmed decision-maker, identified budget, expressed need."
        },
        {
          question: "Where are your offices located?",
          answer: "We have two offices: Paris (Spaces Saint-Lazare, 75008) and Geneva (Spaces Quai de l'Île, 1204). We operate throughout France, Switzerland, Belgium and Luxembourg. Most of our engagements are hybrid: regular on-site presence and remote work."
        },
        {
          question: "What results do you achieve for your clients?",
          answer: "On average, our clients see +47% revenue increase, 3x acceleration in sales cycles, and 89% retention rate. We have generated over €10M in commercial opportunities for our clients since our founding."
        },
        {
          question: "What is the minimum commitment period?",
          answer: "For Sparring Partner, we offer a 12-month program cancellable with 4 weeks notice. For other services, duration varies: 3 to 6 months for commercial architecture, one-off or recurring engagements for coaching. We define together the format suited to your needs."
        },
        {
          question: "Do you only work in French?",
          answer: "No, we work in French and English. Our team supports international clients and can run trainings, coach teams and prospect in both languages. We are used to multicultural contexts."
        },
        {
          question: "How do I start working with FOMOW?",
          answer: "Book a 30-minute discovery call via our online calendar. We analyze your challenges, context and objectives. If there's a fit, we propose a personalized support plan within 48h. Zero commitment before signing."
        },
        {
          question: "What tools and CRMs do you use?",
          answer: "We adapt to your existing stack: HubSpot, Salesforce, Pipedrive, Notion, etc. If you don't have a CRM, we can help you choose and implement one. We also use prospecting tools like Lemlist, Apollo, LinkedIn Sales Navigator."
        }
      ]
    }
  };

  const currentFaq = faqData[i18n.language] || faqData.fr;

  // Générer le Schema.org FAQPage
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": currentFaq.items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      {/* Schema.org FAQPage - Injecté dynamiquement */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <section id="faq" className="py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {currentFaq.sectionTitle}
            </h2>
            <p className="text-lg text-gray-600">
              {currentFaq.sectionSubtitle}
            </p>
          </div>

          {/* FAQ Items - Optimisé AEO avec h3 */}
          <div className="space-y-4">
            {currentFaq.items.map((item, index) => (
              <article 
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:border-gray-300 transition-colors"
                itemScope
                itemType="https://schema.org/Question"
              >
                {/* Question en h3 pour SEO */}
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 md:p-6 text-left group"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 
                    className="text-base md:text-lg font-semibold text-gray-900 pr-4 group-hover:text-[#2A41E5] transition-colors"
                    itemProp="name"
                  >
                    {item.question}
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180 text-[#2A41E5]' : ''
                    }`}
                  />
                </button>

                {/* Réponse concise */}
                <div
                  id={`faq-answer-${index}`}
                  className={`grid transition-all duration-300 ease-in-out ${
                    openIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                  itemScope
                  itemType="https://schema.org/Answer"
                  itemProp="acceptedAnswer"
                >
                  <div className="overflow-hidden">
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                      <p 
                        className="text-gray-600 leading-relaxed"
                        itemProp="text"
                      >
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              {i18n.language === 'fr' 
                ? "Vous avez d'autres questions ?" 
                : "Have more questions?"}
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#2A41E5] text-white font-semibold rounded-xl hover:bg-[#1E32B8] transition-colors"
            >
              {i18n.language === 'fr' ? "Réserver un appel" : "Book a call"}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
